/**
 * Protected organization names (owner directive 2026-08-24).
 *
 * Prest0 itself already has its organization on the platform — nobody may
 * found another one named after it; people who work with Prest0 get INVITED
 * into the existing org. The SERVER is the wall (org mint, governed rename,
 * invite-code minting, redemption label seed — functions/src/rbac/
 * orgFactsCore.ts isProtectedOrgName); this client copy exists so onboarding
 * can refuse the name inline, before the user invests in a doomed save, and
 * so a protected name arriving on an invite profile never PREFILLS anything.
 * Keep the two copies in lockstep — a parity test pins representative
 * verdicts on both.
 *
 * The bar: the digit-zero spelling ("prest0") anywhere in the name is
 * unambiguously the brand; the plain-o spelling ("presto") only as the WHOLE
 * name once company suffixes strip. "Preston" — a real surname that names
 * real firms — stays allowed.
 */

const PROTECTED_SUFFIX_TOKENS = new Set([
  'inc', 'incorporated', 'llc', 'llp', 'ltd', 'limited', 'corp', 'corporation',
  'co', 'company', 'group', 'holdings', 'hq', 'team', 'labs', 'lab',
  'ai', 'tech', 'technologies', 'technology', 'solutions', 'solution',
  'org', 'organization', 'organisation', 'workspace', 'global', 'international',
]);

export function isProtectedOrgName(raw: unknown): boolean {
  if (typeof raw !== 'string' || !raw.trim()) return false;
  const tokens = raw.normalize('NFKC').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  while (tokens.length > 1 && PROTECTED_SUFFIX_TOKENS.has(tokens[tokens.length - 1])) tokens.pop();
  const core = tokens.join('');
  if (!core) return false;
  if (core.includes('prest0')) return true;
  return core === 'presto';
}

/** House-voice line rendered under the name field when the check trips. */
export const PROTECTED_ORG_NAME_MESSAGE =
  'that name belongs to Prest0 itself — enter your own company or team’s name. if you work with Prest0, you’ll be invited into its workspace.';

/** Prefill guard: a protected name arriving from any prefill source
 *  (invite firm profile, cached recommendations, resumed sessions) is
 *  treated as absent — the field stays the user's to fill. */
export function safePrefillOrgName(raw: unknown): string {
  return typeof raw === 'string' && raw.trim() && !isProtectedOrgName(raw) ? raw : '';
}
